import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Search, RotateCcw, Trash2, ShieldOff, Clock, X, Users, ShieldMinus, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const REASONS = [
  { value: 'non_payment', label: 'Non-payment' },
  { value: 'user_request', label: 'User request' },
  { value: 'abuse', label: 'Abuse / TOS' },
  { value: 'duplicate', label: 'Duplicate account' },
  { value: 'other', label: 'Other' },
];

const REASON_LABEL = REASONS.reduce((acc, r) => ({ ...acc, [r.value]: r.label }), {});

const PAGE_SIZE = 25;

export default function UserManagementTable({ onChange }) {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('created_desc');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reason, setReason] = useState('non_payment');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        search,
        sort,
        include_deleted: String(includeDeleted),
        limit: String(PAGE_SIZE),
        offset: String(offset),
      });
      const res = await api.get(`/admin/users?${params}`);
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load users');
    } finally {
      setLoading(false);
    }
  }, [search, sort, includeDeleted, offset]);

  // Debounce search so each keystroke doesn't fire a request
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openDelete = (u) => {
    setDeleteTarget(u);
    setReason('non_payment');
    setNotes('');
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/admin/users/${deleteTarget.user_id}/soft-delete`, {
        reason,
        notes: notes.trim() || undefined,
      });
      toast.success(`Deleted. Permanent removal: ${new Date(res.data.hard_delete_at).toLocaleDateString()}.`);
      setDeleteTarget(null);
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete user');
    } finally {
      setSubmitting(false);
    }
  };

  const restore = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/restore`);
      toast.success(`Restored ${u.email}`);
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not restore');
    }
  };

  const demote = async (u) => {
    if (!window.confirm(`Remove admin privileges from ${u.email}? After this, you'll be able to delete the account from the regular Delete button.`)) {
      return;
    }
    try {
      await api.post(`/admin/users/${u.user_id}/demote`, { confirm: 'DEMOTE ADMIN' });
      toast.success(`${u.email} is no longer an admin`);
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not demote');
    }
  };

  const exportData = async (u) => {
    if (!window.confirm(`Email ${u.email} a full archive of their journal entries and coaching conversations?\n\nThis will send to their email address right now.`)) {
      return;
    }
    try {
      const res = await api.post(`/admin/users/${u.user_id}/export-data`);
      const { journal_entries, conversations, messages } = res.data;
      toast.success(
        `Archive sent to ${u.email} — ${journal_entries} journal entries, ${conversations} conversations (${messages} messages).`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send archive');
    }
  };

  const daysUntil = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso) - new Date();
    return Math.max(0, Math.ceil(ms / 86_400_000));
  };

  const pageInfo = total
    ? `Showing ${offset + 1}–${Math.min(offset + users.length, total)} of ${total}`
    : 'No users';

  return (
    <div className="mystical-card p-6" data-testid="admin-user-management">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-gold" />
          <span className="text-gold font-sans text-xs tracking-[0.25em] uppercase">Users</span>
        </div>
        <span className="font-sans text-[11px] text-text-muted">{pageInfo}</span>
      </div>

      {/* Search + filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
            placeholder="Search email or name..."
            data-testid="user-search-input"
            className="w-full h-10 pl-9 pr-9 bg-sand/50 border border-black/8 focus:border-gold focus:ring-1 focus:ring-gold/30 rounded-xl font-sans text-sm outline-none"
          />
          {search && (
            <button
              onClick={() => { setSearch(''); setOffset(0); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-dark p-1"
              data-testid="user-search-clear"
            >
              <X size={12} />
            </button>
          )}
        </div>
        <select
          value={sort}
          onChange={(e) => { setSort(e.target.value); setOffset(0); }}
          data-testid="user-sort-select"
          className="h-10 px-3 bg-sand/50 border border-black/8 rounded-xl font-sans text-sm outline-none cursor-pointer"
        >
          <option value="created_desc">Newest first</option>
          <option value="created_asc">Oldest first</option>
          <option value="email_asc">Email A→Z</option>
          <option value="email_desc">Email Z→A</option>
        </select>
        <label className="flex items-center gap-2 cursor-pointer px-3 h-10 bg-sand/50 border border-black/8 rounded-xl">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => { setIncludeDeleted(e.target.checked); setOffset(0); }}
            data-testid="include-deleted-checkbox"
            className="accent-gold"
          />
          <span className="font-sans text-[11px] tracking-wider uppercase text-text-muted">Show deleted</span>
        </label>
      </div>

      {/* Table */}
      <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
        {loading && <p className="font-sans text-sm text-text-muted text-center py-8">Loading...</p>}
        {!loading && users.length === 0 && (
          <p className="font-sans text-sm text-text-muted text-center py-8">No users match</p>
        )}
        {!loading && users.map((u) => {
          const days = daysUntil(u.hard_delete_at);
          return (
            <div
              key={u.user_id}
              className={`flex items-center gap-3 p-3 rounded-xl border ${
                u.is_deleted ? 'bg-red-50/30 border-red-200/50' : 'bg-bone/40 border-black/8'
              }`}
              data-testid={`user-row-${u.user_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`font-serif text-base ${u.is_deleted ? 'text-text-muted line-through' : 'text-dark'} truncate`}>
                    {u.name || '(no name)'}
                  </p>
                  {u.is_admin && (
                    <Badge className="bg-gold/15 text-gold border border-gold/20 text-[9px] font-sans">Admin</Badge>
                  )}
                </div>
                <p className="font-sans text-xs text-text-muted truncate">{u.email}</p>
                {u.is_deleted && (
                  <p className="font-sans text-[10px] text-red-600 mt-1 flex items-center gap-1">
                    <Clock size={10} />
                    {REASON_LABEL[u.deleted_reason] || u.deleted_reason} ·
                    {days != null ? ` ${days}d until permanent removal` : ''}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <p className="font-sans text-[10px] text-text-muted">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' }) : '—'}
                </p>
                <div className="flex gap-1">
                  {!u.is_deleted && (
                    <>
                      {u.email_verified ? (
                        <Badge className="bg-moss/15 text-moss border border-moss/20 text-[9px] font-sans">Verified</Badge>
                      ) : (
                        <Badge className="bg-red-50 text-red-600 border border-red-200 text-[9px] font-sans">Unverified</Badge>
                      )}
                      {u.onboarding_completed && (
                        <Badge className="bg-gold/15 text-gold border border-gold/20 text-[9px] font-sans">Onboarded</Badge>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="shrink-0">
                {u.is_deleted ? (
                  <Button
                    onClick={() => restore(u)}
                    size="sm"
                    variant="outline"
                    data-testid={`restore-button-${u.user_id}`}
                    className="h-9 px-3 border-moss/30 text-moss bg-moss/5 hover:bg-moss/10 rounded-lg font-sans text-[10px] tracking-wider uppercase"
                  >
                    <RotateCcw size={12} className="mr-1.5" /> Restore
                  </Button>
                ) : u.is_admin ? (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-bone text-text-muted/70 border border-black/10 text-[9px] font-sans">
                      <ShieldOff size={10} className="mr-1" /> {u.is_protected ? 'Protected' : 'Admin'}
                    </Badge>
                    {!u.is_protected && (
                      <Button
                        onClick={() => demote(u)}
                        size="sm"
                        variant="outline"
                        data-testid={`demote-button-${u.user_id}`}
                        className="h-9 px-3 border-black/15 text-text-muted bg-bone/40 hover:bg-bone rounded-lg font-sans text-[10px] tracking-wider uppercase"
                      >
                        <ShieldMinus size={12} className="mr-1.5" /> Demote
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => exportData(u)}
                      size="sm"
                      variant="outline"
                      data-testid={`export-button-${u.user_id}`}
                      title="Email this user a full archive of their journal entries and coaching conversations"
                      className="h-9 px-3 border-gold/30 text-gold bg-bone/40 hover:bg-gold/10 rounded-lg font-sans text-[10px] tracking-wider uppercase"
                    >
                      <Mail size={12} className="mr-1.5" /> Export
                    </Button>
                    <Button
                      onClick={() => openDelete(u)}
                      size="sm"
                      variant="outline"
                      data-testid={`delete-button-${u.user_id}`}
                      className="h-9 px-3 border-red-300 text-red-700 bg-red-50/30 hover:bg-red-50 rounded-lg font-sans text-[10px] tracking-wider uppercase"
                    >
                      <Trash2 size={12} className="mr-1.5" /> Delete
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-black/8">
          <Button
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0}
            variant="outline"
            size="sm"
            data-testid="user-prev-page"
            className="rounded-lg font-sans text-xs"
          >
            ← Previous
          </Button>
          <Button
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={offset + PAGE_SIZE >= total}
            variant="outline"
            size="sm"
            data-testid="user-next-page"
            className="rounded-lg font-sans text-xs"
          >
            Next →
          </Button>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
          data-testid="user-delete-modal"
          style={{ background: 'rgba(15,16,16,0.55)', backdropFilter: 'blur(8px)' }}
          onClick={() => !submitting && setDeleteTarget(null)}
        >
          <div
            className="bg-bone rounded-t-3xl sm:rounded-3xl p-7 sm:p-8 w-full sm:max-w-lg relative animate-fade-up"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
          >
            <p className="text-red-700 font-sans text-[10px] tracking-[0.35em] uppercase mb-2">Soft-Delete Account</p>
            <h3 className="font-serif text-2xl font-light text-dark leading-tight mb-2 mystical-heading">
              Delete this user?
            </h3>
            <p className="font-sans text-sm text-text-muted leading-relaxed mb-4">
              <span className="text-dark font-medium">{deleteTarget.name || '(no name)'}</span> · {deleteTarget.email}
            </p>
            <p className="font-sans text-xs text-text-muted leading-relaxed mb-5 bg-bone/60 p-3 rounded-xl border border-black/8">
              The account will be <span className="text-dark font-medium">disabled immediately</span> — any active
              sessions are killed and login is blocked. Their data is kept for{' '}
              <span className="text-dark font-medium">30 days</span>, after which it's permanently deleted. You can
              restore the account anytime within that window.
            </p>

            <label className="block mb-4">
              <span className="font-sans text-xs text-text-muted tracking-wider uppercase">Reason</span>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="delete-reason-select"
                className="mt-2 w-full h-11 px-3 bg-sand/50 border border-black/8 focus:border-gold rounded-xl font-sans text-sm outline-none cursor-pointer"
              >
                {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>

            <label className="block mb-5">
              <span className="font-sans text-xs text-text-muted tracking-wider uppercase">Notes (optional)</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                data-testid="delete-notes-input"
                placeholder="Context for the audit log (e.g. invoice number, reported by, ...)"
                rows={3}
                className="mt-2 w-full px-3 py-2 bg-sand/50 border border-black/8 focus:border-gold rounded-xl font-sans text-sm outline-none resize-none"
              />
              <span className="font-sans text-[10px] text-text-muted/70 float-right mt-1">{notes.length}/500</span>
            </label>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-3">
              <Button
                variant="ghost"
                onClick={() => setDeleteTarget(null)}
                disabled={submitting}
                data-testid="delete-cancel-button"
                className="font-sans text-xs text-text-muted h-11"
              >
                Cancel
              </Button>
              <Button
                onClick={submitDelete}
                disabled={submitting}
                data-testid="delete-confirm-button"
                className="bg-red-600 hover:bg-red-700 text-white rounded-xl font-sans text-xs tracking-[0.25em] uppercase h-11 px-6 disabled:opacity-40"
              >
                <Trash2 size={14} className="mr-2" />
                {submitting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
